import './AurigaRoutes.scss';

import { GFTSidePanel } from '@amzn/gft-frontend-commons';
// Local components for Routes
import RouteModalWrapper from '@components/Common/RouteModalWrapper/RouteModalWrapper';
import SystemAlertsWrapper from '@components/Common/SystemAlertsWrapper/SystemAlertsWrapper';
import AurigaError from '@components/Error/AurigaError';
import Landing from '@components/Landing/Landing';
import { AuditHistoryContextProvider } from '@context/AuditHistoryContext';
import { DeckContextProvider } from '@context/DeckContext';
import { DecklinesContextProvider } from '@context/DecklinesContext';
import { FootnoteProvider } from '@context/FootnotesContext';
import { HighlightingProvider } from '@context/HighlightingContext';
import { KPILibraryProvider } from '@context/KPILibraryContext';
import { KPISubscriptionProvider } from '@context/KPISubscriptionContext';
import { PageContextProvider } from '@context/PageContext';
import { PageSetupContextProvider } from '@context/PageSetupContext';
import { ScenarioManagementContextProvider } from '@context/ScenarioManagementContext';
import { TOCContextProvider } from '@context/TOCContext';
import AuditHistory from '@deckManagement/components/AuditHistory/AuditHistory';
import DeckIndex from '@deckManagement/components/Deck/DeckIndex';
import DeckSidePanel from '@deckManagement/components/Deck/SidePanel/DeckSidePanel';
import KPILibrary from '@deckManagement/components/KPILibrary/KPILibrary';
import Page from '@deckManagement/components/Page/Page';
import PageSidePanel from '@deckManagement/components/Page/SidePanel/PageSidePanel';
import PageSetupWizard from '@deckManagement/components/PageSetupWizard/PageSetupWizard';
import ScenarioManagement from '@deckManagement/components/ScenarioManagement/ScenarioManagement';
import TableOfContentSidePanel from '@deckManagement/components/TOC/SidePanel/TableOfContentSidePanel';
import TableOfContents from '@deckManagement/components/TOC/TableOfContents';
import { KPILibraryMode } from '@deckManagement/types/KPILibrary.types';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import HomeIcon from '@mui/icons-material/Home';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { forEach } from 'lodash';
import { ComponentType, Context, ContextType, ElementType, ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AurigaRoute, RouteWrapperProps } from 'types/AurigaRoutes.types';

/**
 * A common side panel header for all routes in Auriga.
 * Note: Mocks do not show this change, but understand each route
 * can have a different header content based on this setup, and in how
 * GFTSidePanel is implemented.
 */
const CommonAurigaSideHeader = (
  <Link className='deck-prep-home-link' to='/deck-preparation'>
    <Typography variant='h6' className='home-header-text'>
      <HomeIcon className='home-header-icon' />
      Deck Preparation
    </Typography>
  </Link>
);

const DEFAULT_SIDE_HEADER_COLOR = '#001833';
const DEFAULT_SIDE_CONTENT_COLOR = '#002855';
const DEFAULT_SIDE_MAIN_CONTENT_PADDING = '8px';
const GFT_PRIMARY_BG_50_PERCENT = 'var(--auriga-surface-tint)';

/**
 * Wrapper for 404 Not Found Pages
 * Uses the common AurigaError Component
 */
const NotFoundErrorWrapper = () => {
  useRecordLoadComplete(true);
  return <AurigaError title='404 - Not Found' errorMessage='The page you are looking for does not exist.' />;
};

const routes: Array<AurigaRoute> = [
  {
    path: '/',
    name: 'Home',
    component: Landing,
    hasSidePanel: false,
    hasMainContentPadding: false,
  },
  {
    path: ['/deck-preparation', '/deck-preparation/decks'],
    name: 'Deck Preparation index',
    component: DeckIndex,
    sidePanelHeaderContent: CommonAurigaSideHeader,
    sidePanelContent: <DeckSidePanel />,
    hasSidePanel: true,
    hasMainContentPadding: true,
    contextProviders: [DeckContextProvider],
  },
  {
    path: '/deck-preparation/decks/:deckId/toc',
    name: 'Page Library',
    component: TableOfContents,
    sidePanelHeaderContent: CommonAurigaSideHeader,
    sidePanelContent: <TableOfContentSidePanel />,
    sidePanelContentBgColor: GFT_PRIMARY_BG_50_PERCENT,
    sidePanelMainContentPadding: '0',
    hasSidePanel: true,
    hasMainContentPadding: true,
    showsSystemAlerts: true,
    contextProviders: [DeckContextProvider, AuditHistoryContextProvider, TOCContextProvider, HighlightingProvider],
  },
  {
    path: '/deck-preparation/decks/:deckId/page-setup/new',
    name: 'Page Setup',
    component: PageSetupWizard,
    sidePanelHeaderContent: CommonAurigaSideHeader,
    sidePanelContent: <TableOfContentSidePanel />,
    sidePanelContentBgColor: GFT_PRIMARY_BG_50_PERCENT,
    sidePanelMainContentPadding: '0',
    hasSidePanel: true,
    hasMainContentPadding: true,
    showsSystemAlerts: true,
    contextProviders: [DeckContextProvider, AuditHistoryContextProvider, TOCContextProvider, PageSetupContextProvider],
  },
  {
    path: '/deck-preparation/decks/:deckId/page-setup/:pageName',
    name: 'Page Setup - Edit',
    component: PageSetupWizard,
    sidePanelHeaderContent: CommonAurigaSideHeader,
    sidePanelContent: <TableOfContentSidePanel />,
    sidePanelContentBgColor: GFT_PRIMARY_BG_50_PERCENT,
    sidePanelMainContentPadding: '0',
    hasSidePanel: true,
    hasMainContentPadding: true,
    showsSystemAlerts: true,
    contextProviders: [DeckContextProvider, AuditHistoryContextProvider, TOCContextProvider, PageSetupContextProvider],
  },
  {
    path: '/deck-preparation/decks/:deckId/pages/:pageName',
    name: 'Page Item',
    component: Page,
    sidePanelHeaderContent: CommonAurigaSideHeader,
    sidePanelContent: <PageSidePanel />,
    sidePanelContentBgColor: GFT_PRIMARY_BG_50_PERCENT,
    sidePanelMainContentPadding: '0',
    keepSidePanelContentMounted: true, // Preserve side panel tab/state across collapse/expand
    hasSidePanel: true,
    hasMainContentPadding: true,
    showsSystemAlerts: true,
    contextProviders: [
      DeckContextProvider,
      TOCContextProvider,
      PageContextProvider,
      FootnoteProvider,
      AuditHistoryContextProvider,
      KPISubscriptionProvider,
      KPILibraryProvider,
      DecklinesContextProvider,
      HighlightingProvider,
    ],
  },
  {
    path: '/deck-preparation/decks/:deckId/scenario-management',
    name: 'Scenario Management',
    component: ScenarioManagement,
    sidePanelHeaderContent: CommonAurigaSideHeader,
    sidePanelContent: <TableOfContentSidePanel />,
    sidePanelContentBgColor: GFT_PRIMARY_BG_50_PERCENT,
    sidePanelMainContentPadding: '0',
    hasSidePanel: true,
    hasMainContentPadding: true,
    showsSystemAlerts: true,
    contextProviders: [DeckContextProvider, TOCContextProvider, ScenarioManagementContextProvider],
  },
  {
    path: '/deck-preparation/decks/:deckId/pages/:pageName/audit-history',
    name: 'Page Audit History - :pageName',
    component: AuditHistory,
    hasSidePanel: false,
    hasMainContentPadding: true,
    isModal: true, // Renders this route in a modal using RouteModalWrapper
    contextProviders: [
      DeckContextProvider,
      PageContextProvider,
      FootnoteProvider,
      AuditHistoryContextProvider,
      DecklinesContextProvider,
    ],
  },
  {
    path: '/deck-preparation/decks/:deckId/audit-history',
    name: 'Deck Audit History - :deckId',
    component: AuditHistory,
    hasSidePanel: false,
    hasMainContentPadding: true,
    isModal: true, // Renders this route in a modal using RouteModalWrapper
    parentRoute: '/deck-preparation/decks/:deckId/toc', // Navigate to TOC when modal closes
    contextProviders: [DeckContextProvider, AuditHistoryContextProvider],
  },
  {
    path: '/deck-preparation/decks/:deckId/pages/:pageName/decklines/:decklineId/audit-history',
    name: 'Deckline Audit History - Page: :pageName - Deckline: :decklineId',
    component: AuditHistory,
    hasSidePanel: false,
    hasMainContentPadding: true,
    isModal: true, // Renders this route in a modal using RouteModalWrapper
    parentRoute: '/deck-preparation/decks/:deckId/pages/:pageName', // Navigate to defined Parent Route page when modal closes
    contextProviders: [
      DeckContextProvider,
      PageContextProvider,
      FootnoteProvider,
      AuditHistoryContextProvider,
      DecklinesContextProvider,
    ],
  },
  {
    path: '/deck-preparation/kpi-library',
    name: 'KPI Library',
    component: () => <KPILibrary mode={KPILibraryMode.PAGE} />,
    hasSidePanel: false,
    hasMainContentPadding: false,
    showsSystemAlerts: true,
    contextProviders: [KPISubscriptionProvider, KPILibraryProvider],
  },
  // Not Found Page 404 routing catch should remain the last route.
  {
    path: '*',
    name: 'Page Not Found',
    component: NotFoundErrorWrapper,
    hasSidePanel: false,
    hasMainContentPadding: true,
  },
];

const RouteWrapper = ({
  hasMainContentPadding,
  hasSidePanel,
  isSidePanelOpen,
  onSidePanelToggle,
  sidePanelHeaderContent,
  sidePanelHeaderBgColor,
  sidePanelMainContentPadding,
  sidePanelContent,
  sidePanelContentBgColor,
  keepSidePanelContentMounted,
  showsSystemAlerts,
  RouteComponent,
}: RouteWrapperProps) => {
  const { deckId } = useParams<{ deckId: string }>();

  // Memoize the routed page so a side-panel toggle (which re-renders RouteWrapper) doesn't re-render
  // it — the page never depends on panel state. RouteComponent is a stable module ref, so React reuses this.
  const routeContent = useMemo(
    () =>
      showsSystemAlerts ? (
        <SystemAlertsWrapper>
          <RouteComponent />
        </SystemAlertsWrapper>
      ) : (
        <RouteComponent />
      ),
    [RouteComponent, showsSystemAlerts],
  );

  return (
    <Box className='container'>
      {hasSidePanel && (
        <GFTSidePanel
          headerContent={sidePanelHeaderContent}
          headerBackgroundColor={sidePanelHeaderBgColor || DEFAULT_SIDE_HEADER_COLOR}
          contentBackgroundColor={sidePanelContentBgColor || DEFAULT_SIDE_CONTENT_COLOR}
          contentPadding={sidePanelMainContentPadding || DEFAULT_SIDE_MAIN_CONTENT_PADDING}
          isOpen={isSidePanelOpen}
          onToggle={onSidePanelToggle}
          keepContentMounted={keepSidePanelContentMounted}
          className='side-panel'
        >
          {typeof sidePanelContent === 'function' ? sidePanelContent({ deckId: deckId || '' }) : sidePanelContent}
        </GFTSidePanel>
      )}
      <Box
        className={`main-content ${
          hasSidePanel ? `-with-side-panel ${isSidePanelOpen ? '-expanded' : '-collapsed'}` : ''
        } ${hasMainContentPadding ? '-padded' : ''}`}
      >
        {routeContent}
      </Box>
    </Box>
  );
};

// As contexts have different prop types, keeping the generic type 'any'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wrapComponentWithProviders = (Component: ReactNode, providers: Array<ContextType<Context<any>>>): ReactNode => {
  return providers.reduceRight((children, Provider) => <Provider>{children}</Provider>, Component);
};

/**
 * Parent Routing Component that redirects direct Modal Route access to a Parent Route with the Modal state open
 * - This allows direct Modal URL access (e.g. /deck-preparation/decks/1/pages/1/audit-history)
 * - to load the Parent Route and open the Modal on any route visit
 */
const ModalParentRedirectComponent = ({ parentRoute }: { parentRoute?: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    // Determine the parent path from parentRoute prop or fallback to removing the last URL segment to determine parent
    let parentPath: string;

    if (parentRoute) {
      // Replace route params in parentRoute with actual values
      parentPath = parentRoute.replace(/:[a-zA-Z]+/g, (match) => {
        const paramName = match.slice(1);
        return params[paramName] || match;
      });
    } else {
      // Fallback to default behavior which removes the last URL segment
      const pathSegments = location.pathname.split('/');
      parentPath = pathSegments.slice(0, -1).join('/');
    }

    const modalPath = location.pathname;

    // Navigate to the modal route with the background state pointing to the parent
    navigate(modalPath, {
      state: {
        background: {
          pathname: parentPath,
        },
      },
      replace: true,
    });
  }, [navigate, location, parentRoute, params]);

  return null;
};

/**
 * Utility function to create redirect components with any parent route as specified
 */
const createModalParentRedirectComponent = (parentRoute?: string): ElementType => {
  return (() => <ModalParentRedirectComponent parentRoute={parentRoute} />) as ElementType;
};

/**
 * Auriga Routes Component
 * - Renders all defined routes
 * - Handles Side Panel state and toggle
 * - Handles Modal Routes
 */
const AurigaRoutes = () => {
  // Centralizes Side Panel control across application
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(true);
  // To support Routes rendering within a modal
  const location = useLocation();
  const background = location.state?.background;

  const handleSidePanelToggle = (isOpen: boolean) => {
    setIsSidePanelOpen(isOpen);
  };

  // Separate out Regular Routes from enabled Modal Routes
  const regularRoutes: AurigaRoute[] = [];
  const modalRoutes: AurigaRoute[] = [];
  forEach(routes, (route) => {
    if (route.isModal) {
      modalRoutes.push(route);
    } else {
      regularRoutes.push(route);
    }
  });

  // Add parent redirect routes for direct modal URL access (this loads the parent and opens the modal)
  const modalParentRedirectRoutes = modalRoutes.map((route) => ({
    ...route,
    component: createModalParentRedirectComponent(route.parentRoute),
    isModal: false,
  }));

  return (
    <Box className='auriga-routes'>
      {/* Regular Routes and Modal Parent Redirect Routes: Use background location if modal is open, or current location */}
      <Routes location={background || location}>
        {[...regularRoutes, ...modalParentRedirectRoutes].map(
          ({
            path,
            name,
            component,
            sidePanelHeaderContent,
            sidePanelHeaderBgColor,
            sidePanelContent,
            sidePanelContentBgColor,
            sidePanelMainContentPadding,
            hasSidePanel,
            hasMainContentPadding,
            keepSidePanelContentMounted,
            showsSystemAlerts,
            contextProviders,
          }: AurigaRoute) => {
            const routeElement = (
              <RouteWrapper
                hasMainContentPadding={hasMainContentPadding}
                hasSidePanel={hasSidePanel}
                isSidePanelOpen={isSidePanelOpen}
                onSidePanelToggle={handleSidePanelToggle}
                sidePanelHeaderContent={sidePanelHeaderContent}
                sidePanelHeaderBgColor={sidePanelHeaderBgColor}
                sidePanelMainContentPadding={sidePanelMainContentPadding}
                sidePanelContent={sidePanelContent}
                sidePanelContentBgColor={sidePanelContentBgColor}
                keepSidePanelContentMounted={keepSidePanelContentMounted}
                showsSystemAlerts={showsSystemAlerts}
                RouteComponent={component as ComponentType}
              />
            );
            // Handle case where 'path' is an array of string paths applied to a route
            if (Array.isArray(path)) {
              return path.map((_path) => (
                <Route
                  path={_path}
                  key={`${_path}-${name}`}
                  element={contextProviders ? wrapComponentWithProviders(routeElement, contextProviders) : routeElement}
                />
              ));
            }
            // Standard single string 'path' case
            return (
              <Route
                path={path}
                key={`${path}-${name}`}
                element={contextProviders ? wrapComponentWithProviders(routeElement, contextProviders) : routeElement}
              />
            );
          },
        )}
      </Routes>

      {/* Modal Routes: Only render when background location exists (i.e. navigated from another route) */}
      {background && (
        <Routes>
          {modalRoutes.map(({ path, name, component: ModalComponent, contextProviders, parentRoute }) => {
            const modalContent = <ModalComponent />;
            const wrappedModal = (
              <RouteModalWrapper title={name} parentRoute={parentRoute}>
                {modalContent}
              </RouteModalWrapper>
            );

            return (
              <Route
                path={path as string} // Modal Routes are only expected to pass a single route path string
                key={`modal-${path}-${name}`}
                element={contextProviders ? wrapComponentWithProviders(wrappedModal, contextProviders) : wrappedModal}
              />
            );
          })}
        </Routes>
      )}
    </Box>
  );
};

export default AurigaRoutes;
